/**
 * Seeded PRNG — mulberry32.
 *
 * This is the ONLY source of randomness in VECTOR ZERO. `Math.random` is banned by lint
 * (see eslint.config.js) because the simulation must reproduce exactly from a seed for
 * replay, scenario tests, and bug reports (SPEC §4.3, §4.6).
 *
 * The whole generator state is a single uint32, so a replay keyframe can store it in 4 bytes.
 */

const SQRT_3 = Math.sqrt(3);

/**
 * Stateless hash of an integer to a float in [0, 1). Unitless.
 *
 * One mulberry32 step with no carried state, for the places that want a reproducible value *of*
 * something — a frame index, a block index — rather than the next draw from a stream. Two calls
 * with the same arguments always agree, so a downlink artefact can be recomputed from simulated
 * time without keeping a generator alive or allocating one per frame.
 */
export function hashUnit(value: number, salt = 0): number {
  let t = (Math.imul(value | 0, 0x9e3779b1) ^ Math.imul(salt | 0, 0x85ebca6b)) | 0;
  t = (t + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), 1 | t);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export class Rng {
  /** Current 32-bit state. Held unsigned. */
  private state: number;

  /** Seed the generator (any 32-bit integer; the low 32 bits are used). */
  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Uniform float in [0, 1). Unitless. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [minInclusive, maxExclusive). Units are the caller's. */
  range(minInclusive: number, maxExclusive: number): number {
    return minInclusive + this.next() * (maxExclusive - minInclusive);
  }

  /** Uniform integer in [minInclusive, maxInclusive]. */
  int(minInclusive: number, maxInclusive: number): number {
    return minInclusive + Math.floor(this.next() * (maxInclusive - minInclusive + 1));
  }

  /** True with the given probability in [0, 1]. */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  /**
   * Approximately standard-normal sample (Irwin–Hall, n = 4): mean 0, variance 1,
   * support bounded to ±3.46σ. Bounded support is a feature here — an unbounded
   * Box–Muller tail can hand a guidance loop a 6σ outlier that reads as a bug.
   */
  gaussian(): number {
    const sum = this.next() + this.next() + this.next() + this.next();
    return (sum - 2) * SQRT_3;
  }

  /** Snapshot the state for a replay keyframe (uint32). */
  save(): number {
    return this.state >>> 0;
  }

  /** Restore a state previously produced by save(). */
  restore(state: number): void {
    this.state = state >>> 0;
  }

  /** An independent generator continuing from this one's current state. */
  clone(): Rng {
    return new Rng(this.state);
  }

  /**
   * Derive an independent, reproducible sub-generator — for per-site or per-missile
   * streams that must not be perturbed by unrelated call ordering elsewhere.
   */
  fork(streamId: number): Rng {
    return new Rng((Math.imul(this.state ^ streamId, 0x9e3779b1) ^ (streamId << 16)) >>> 0);
  }
}
